import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import AddressGeoApi from 'components/AddressGeoApi/AddressGeoApi';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import StyledInput from 'components/styledInput/StyledInput';
import { buildCompanySupplierFormOptions } from 'constants/missionSupplierFilters';
import {
  diffCompanyOverview,
  isCompanyOverviewValid,
  validateCompanyOverview,
} from 'helper/companyOverviewDiff';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

const STATUS_LIST = [
  { value: null, label: 'Choisir' },
  { value: 'SAS', label: 'SAS' },
  { value: 'SASU', label: 'SASU' },
  { value: 'SARL', label: 'SARL' },
  { value: 'EURL', label: 'EURL' },
  { value: 'SEARL', label: 'SEARL' },
  { value: 'EI', label: 'EI' },
  { value: 'SAI', label: 'SAI' },
  { value: 'GIE', label: 'GIE' },
  { value: 'SCI', label: 'SCI' },
  { value: 'SCOP', label: 'SCOP' },
  { value: 'SICA', label: 'SICA' },
  { value: 'SMIA', label: 'SMIA' },
  { value: 'SNC', label: 'SNC' },
  { value: 'Association', label: 'Association' },
  { value: 'Administration', label: 'Administration' },
  { value: 'Société étrangère', label: 'Société étrangère' },
  { value: 'AUTRES', label: 'AUTRES' },
];

const draftFromCompany = (companyInfo) => ({
  reason_social: companyInfo?.reason_social ?? '',
  name_commercial: companyInfo?.name_commercial ?? '',
  address: companyInfo?.address ?? null,
  siret: companyInfo?.siret ?? '',
  status: companyInfo?.status ?? null,
  code_ape: companyInfo?.code_ape ?? '',
  staff: companyInfo?.staff ?? '',
  conv_collective: companyInfo?.conv_collective ?? null,
  companySupplier: companyInfo?.companySupplier?._id?.toString() ?? null,
});

const ModalCompanyLegalInformation = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();
  const companyInfo = modalData?.companyInfo;
  const [draft, setDraft] = useState(() => draftFromCompany(companyInfo));
  const [supplierList, setSupplierList] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem('xsrfToken');

  useEffect(() => {
    setDraft(draftFromCompany(companyInfo));
  }, [companyInfo]);

  useEffect(() => {
    ApiDatabase.getCompanySuppliers(
      {},
      (data) => setSupplierList(Array.isArray(data) ? data : []),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, []);

  useEffect(() => {
    ApiDatabase.getCollectiveAgreement(
      { token },
      (data) =>
        setAgreements(
          (Array.isArray(data) ? data : []).map((item) => ({
            // Read and write use different keys on purpose: the company
            // record stores the IDCC number, which is what `checked` matches
            // below, while the update endpoint takes the Mongo id and resolves
            // the IDCC itself.
            id: item._id,
            name: `${item.IDCC_id} - ${item.IDCC_name}`,
            checked:
              parseInt(companyInfo?.conv_collective) === parseInt(item.IDCC_id),
          })),
        ),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, [token, companyInfo?.conv_collective]);

  const editField = (field) => (event) =>
    setDraft((previous) => ({ ...previous, [field]: event.target.value }));

  const selectAgreement = (updated) => {
    // StyledInput hands back its own shape — { value, label, checked } — and
    // storing that as the agreement list left the next render mapping
    // undefined ids and names, so the list came back empty. Only the ticked
    // state is taken from it; the entries keep theirs.
    const selected = updated.find((agreement) => agreement.checked);
    setAgreements((previous) =>
      previous.map((agreement) => ({
        ...agreement,
        checked: agreement.id === selected?.value,
      })),
    );
    setDraft((previous) => ({
      ...previous,
      conv_collective: selected ? selected.value : null,
    }));
  };

  // Companies carry INSEE wordings the hardcoded list never had ("SA à conseil
  // d'administration (s.a.i.)"). Without its own option the select falls back
  // to "Choisir", and a filled field reads as empty.
  const statusOptions =
    !companyInfo?.status ||
    STATUS_LIST.some((option) => option.value === companyInfo.status)
      ? STATUS_LIST
      : [
          ...STATUS_LIST,
          { value: companyInfo.status, label: companyInfo.status },
        ];

  const errors = validateCompanyOverview(draft);
  const modifiedFields = diffCompanyOverview(draft, companyInfo);
  const canConfirm =
    isCompanyOverviewValid(errors) && Object.keys(modifiedFields).length > 0;

  const submit = () => {
    if (!canConfirm) return;
    setIsLoading(true);
    ApiDatabase.postUpdateCompanyOverview(
      { token, idCompany: companyInfo?._id, companyInfo: modifiedFields },
      () => {
        setIsLoading(false);
        toast.success(
          "Les informations de l'entreprise ont été mises à jour avec succès.",
        );
        // The tab renders labels, not ids: the patch is resolved here, where
        // the reference lists are already loaded, rather than in the tab.
        const resolved = { ...modifiedFields };
        if (modifiedFields.conv_collective) {
          const agreement = agreements.find(
            (item) =>
              item.id?.toString() ===
              modifiedFields.conv_collective?.toString(),
          );
          if (agreement) resolved.conv_collective = agreement.name;
        }
        if ('companySupplier' in modifiedFields) {
          resolved.companySupplier = modifiedFields.companySupplier
            ? supplierList.find(
                (supplier) =>
                  supplier._id?.toString() ===
                  modifiedFields.companySupplier?.toString(),
              ) || null
            : null;
        }
        setModalDataReturn({
          type: 'company_legal_information_updated',
          companyInfo: resolved,
        });
        toggle();
      },
      (err) => {
        setIsLoading(false);
        console.error(err);
        toast.error(
          'Une erreur est survenue lors de la mise à jour des informations.',
        );
      },
    );
  };

  const selectedAgreementLabel =
    agreements.find((agreement) => agreement.checked)?.name || '-';

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Informations légales
      </Modalheader>
      <ModalBody>
        {/* The modal structure sets no height of its own, so the body only
            scrolls once something bounds it. Bounded here rather than in the
            structure, which every modal of the admin shares. The dropdowns are
            unaffected: Popper positions them against the viewport. */}
        <div className={'flex flex-col gap-4 max-h-[60vh] overflow-y-auto'}>
          <label className={'flex flex-col gap-1'}>
            Raison sociale
            <StyledInput
              type={'text'}
              value={draft.reason_social}
              onChange={editField('reason_social')}
            />
          </label>
          <label className={'flex flex-col gap-1'}>
            Nom commercial
            <StyledInput
              type={'text'}
              value={draft.name_commercial}
              onChange={editField('name_commercial')}
            />
          </label>
          <div className={'flex flex-col gap-1'}>
            Adresse
            <AddressGeoApi
              addressGeoValue={(address) =>
                setDraft((previous) => ({ ...previous, address }))
              }
              addressValue={draft.address}
              page={'editCompany'}
            />
          </div>
          <label className={'flex flex-col gap-1'}>
            SIRET
            <StyledInput
              type={'number'}
              value={draft.siret}
              onChange={editField('siret')}
              error={errors.errorSiret}
            />
          </label>
          <label className={'flex flex-col gap-1'}>
            Forme juridique
            <StyledInput
              type={'select'}
              value={draft.status}
              list={statusOptions}
              onChange={editField('status')}
            />
          </label>
          <label className={'flex flex-col gap-1'}>
            Fournisseur
            <StyledInput
              type={'select'}
              value={draft.companySupplier || ''}
              list={buildCompanySupplierFormOptions(supplierList)}
              onChange={(event) =>
                setDraft((previous) => ({
                  ...previous,
                  companySupplier: event.target.value || null,
                }))
              }
            />
          </label>
          <label className={'flex flex-col gap-1'}>
            Code APE
            <StyledInput
              type={'text'}
              value={draft.code_ape}
              onChange={editField('code_ape')}
              error={errors.errorCodeApe}
            />
          </label>
          <label className={'flex flex-col gap-1'}>
            Effectif
            <StyledInput
              type={'number'}
              value={draft.staff}
              onChange={editField('staff')}
              error={errors.errorStaff}
            />
          </label>
          <div className={'flex flex-col gap-1'}>
            Convention collective
            <StyledInput
              type={'selectMultipleRadio'}
              value={selectedAgreementLabel}
              list={agreements.map((agreement) => ({
                value: agreement.id,
                label: agreement.name,
                checked: agreement.checked,
              }))}
              onChange={selectAgreement}
              isSearchable
              dropdownFullWidth
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button size={'LG'} color={'SECONDARY'} onClick={toggle}>
          Annuler
        </Button>
        {isLoading ? (
          <Button size={'LG'} color={'DISABLED'}>
            Confirmation ...
          </Button>
        ) : (
          <Button
            size={'LG'}
            color={canConfirm ? 'PRIMARY' : 'DISABLED'}
            onClick={submit}
          >
            Confirmer
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalCompanyLegalInformation;
